import {PublicationContext} from 'centrifuge';
import {camelizeKeys, decamelizeKeys} from 'humps';
import {noop} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {z as r} from 'zod';

import {useCentrifugeSubscription} from '../../../../../../../provider/centrifuge/centrifuge-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {useSnackbar} from '../../../../../../../provider/snackbar/snackbar-hook';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../../../../../../../service/api/api-const';
import {ApiError} from '../../../../../../../util/error';
import {FetchMethodEnum, fetchNoContent} from '../../../../../../../util/fetch';

const SUGGEST_TIMEOUT_MS = 120_000;

const reviewAiSuggestsSchema = r.object({
    error: r.string().nullable(),
    requestId: r.string(),
    stop: r.boolean(),
    text: r.string(),
});

type ReviewAiSuggestType = r.infer<typeof reviewAiSuggestsSchema>;

/**
 * The suggestion endpoint answers 400 for several unrelated reasons, telling them apart only by the
 * `status` text. A missing licence is the one worth reporting separately: it is not a transient
 * failure and retrying will never help.
 */
export enum ReviewAiSuggestErrorReasonEnum {
    NoLicence = 'noLicence',
    Unknown = 'unknown',
}

function getSuggestErrorReason(error: unknown): ReviewAiSuggestErrorReasonEnum {
    if (!(error instanceof ApiError)) {
        return ReviewAiSuggestErrorReasonEnum.Unknown;
    }

    const status = (error.jsonData as {status?: unknown} | null)?.status;

    return typeof status === 'string' && status.toLowerCase().includes('no active license')
        ? ReviewAiSuggestErrorReasonEnum.NoLicence
        : ReviewAiSuggestErrorReasonEnum.Unknown;
}

/**
 * Lets a caller (bulk generation) follow a single review's generation to its end instead of
 * relying on the shared `isBulkGenerate` flag. When `onError` is provided the caller owns error
 * reporting and the hook stays silent — otherwise one bulk run would raise a snackbar per review.
 */
export type ReviewAiSuggestCallbacksType = {
    onComplete?: (reviewId: number) => void;
    onError?: (reviewId: number, reason: ReviewAiSuggestErrorReasonEnum) => void;
};

type ReviewGenerateInfoType = {
    channelId: string;
    reviewId: number;
    isGenerating: boolean;
    callbacks?: ReviewAiSuggestCallbacksType;
};

function requestReviewSuggest(options: {reviewId: number; channelId: string; requestId: string}): Promise<void> {
    return fetchNoContent(`${rootApiUrl}/cp/neuronetwork_replies/create_suggestion/`, {
        method: FetchMethodEnum.post,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(decamelizeKeys(options)),
    });
}

function isReviewSuggestAction(data: unknown): data is ReviewAiSuggestType {
    return reviewAiSuggestsSchema.safeParse(data).success;
}

function getCentrifugeReviewSuggestChannel(options: {channelId: string}) {
    return `review_suggestions:suggest_${options.channelId}`;
}

type FinishGenerationOptionsType = {
    isSuccess: boolean;
    reason?: ReviewAiSuggestErrorReasonEnum;
    reviewId: number;
    callbacks?: ReviewAiSuggestCallbacksType;
    removeListener: VoidFunction;
    onStopGenerating: (reviewId: number) => void;
    onUnmanagedError: VoidFunction;
};

function finishGeneration(options: FinishGenerationOptionsType): void {
    const {isSuccess, reason, reviewId, callbacks, removeListener, onStopGenerating, onUnmanagedError} = options;

    removeListener();
    onStopGenerating(reviewId);

    if (isSuccess) {
        callbacks?.onComplete?.(reviewId);

        return;
    }

    if (callbacks?.onError) {
        callbacks.onError(reviewId, reason ?? ReviewAiSuggestErrorReasonEnum.Unknown);

        return;
    }

    onUnmanagedError();
}

type ReviewAiSuggestHookType = {
    requestSuggest: (reviewId: number, callbacks?: ReviewAiSuggestCallbacksType) => void;
    isGeneratingForReview: (reviewId: number) => boolean;
    isBulkGenerate: boolean;
};

export function useNewReviewAiSuggest(
    setAnswerText: (value: string, reviewPk: number) => void
): ReviewAiSuggestHookType {
    const [isGeneratingSuggest, setIsGeneratingSuggest] = useState<boolean>(false);
    const {getSubscription} = useCentrifugeSubscription();
    const {snackbar} = useSnackbar();

    const reviewsGenerateInfoRef = useRef<Array<ReviewGenerateInfoType>>([]);

    const updateGeneratingStatus = useCallback((reviewId: number, isGenerating: boolean) => {
        reviewsGenerateInfoRef.current = reviewsGenerateInfoRef.current.map((item) =>
            item.reviewId === reviewId ? {...item, isGenerating} : item
        );
    }, []);

    const showSuggestErrorSnackbar = useCallback(() => {
        snackbar.error({
            message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
            description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
        });
    }, [snackbar]);

    const handleSuggestError = useCallback(() => {
        setIsGeneratingSuggest(false);

        // Callers that report errors themselves (bulk generation) must not also raise a snackbar,
        // otherwise a failed batch stacks one toast per review.
        const hasUnmanagedGeneration = reviewsGenerateInfoRef.current.some(
            (item) => item.isGenerating && !item.callbacks?.onError
        );

        if (hasUnmanagedGeneration) {
            showSuggestErrorSnackbar();
        }
    }, [showSuggestErrorSnackbar]);

    useEffect(() => {
        if (!isGeneratingSuggest) {
            return noop;
        }

        const timeoutId = setTimeout(handleSuggestError, SUGGEST_TIMEOUT_MS);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [handleSuggestError, isGeneratingSuggest]);

    const requestSuggest = useCallback(
        (reviewId: number, callbacks?: ReviewAiSuggestCallbacksType) => {
            const channelId = uuidv4();

            const subscription = getSubscription(getCentrifugeReviewSuggestChannel({channelId}));

            let removeListener: VoidFunction = noop;

            function stopGenerating(finishedReviewId: number) {
                updateGeneratingStatus(finishedReviewId, false);
                setIsGeneratingSuggest(false);
            }

            function finish(isSuccess: boolean, reason?: ReviewAiSuggestErrorReasonEnum) {
                finishGeneration({
                    isSuccess,
                    reason,
                    reviewId,
                    callbacks,
                    removeListener,
                    onStopGenerating: stopGenerating,
                    onUnmanagedError: showSuggestErrorSnackbar,
                });
            }

            function onActionReceived({data}: PublicationContext) {
                const camelcaseData = camelizeKeys(data);

                if (!isReviewSuggestAction(camelcaseData)) {
                    return;
                }

                const generateItem = reviewsGenerateInfoRef.current.find(
                    (item) => item.channelId === camelcaseData.requestId
                );

                if (camelcaseData.error) {
                    finish(false);

                    return;
                }

                if (generateItem?.reviewId) {
                    setAnswerText(camelcaseData.text, generateItem.reviewId);
                }

                if (camelcaseData.stop) {
                    finish(true);
                }
            }

            subscription?.on('publication', onActionReceived);

            removeListener = () => {
                subscription?.removeListener('publication', onActionReceived);
            };

            // Очищаем старые завершенные генерации для этого reviewId
            reviewsGenerateInfoRef.current = reviewsGenerateInfoRef.current.filter(
                (item) => item.reviewId !== reviewId || item.isGenerating
            );

            reviewsGenerateInfoRef.current.push({reviewId, channelId, isGenerating: true, callbacks});
            setIsGeneratingSuggest(true);

            requestReviewSuggest({reviewId, channelId, requestId: channelId}).catch((error: unknown) => {
                finish(false, getSuggestErrorReason(error));
            });

            setTimeout(() => {
                const isStillGenerating = reviewsGenerateInfoRef.current.some(
                    (item) => item.channelId === channelId && item.isGenerating
                );

                if (isStillGenerating) {
                    finish(false);
                } else {
                    removeListener();
                }
            }, SUGGEST_TIMEOUT_MS);
        },
        [getSubscription, updateGeneratingStatus, setAnswerText, showSuggestErrorSnackbar]
    );

    const isGeneratingForReview = useCallback((reviewId: number) => {
        return reviewsGenerateInfoRef.current.some((item) => item.reviewId === reviewId && item.isGenerating);
    }, []);

    const isBulkGenerate = reviewsGenerateInfoRef.current.some((item) => item.isGenerating);

    return {
        requestSuggest,
        isGeneratingForReview,
        isBulkGenerate,
    };
}
